
import React, { useMemo, useState } from "react";
import TimelineHeader from "./TimelineHeader";
import RoomTypeGroup from "./RoomTypeGroup";
import {
  makeDateRange,
  addDays,
  toISODate,
} from "../utils/timelineUtils";

const getLocalDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const parseLocalDate = (value) => {
  if (!value) return null;

  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) return null;

  return new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    12
  );
};

function assignBookingLanes(stays = []) {
  const grouped = new Map();

  stays.forEach((stay) => {
    if (String(stay.status || "").toUpperCase() === "CANCELLED") {
      return;
    }

    const roomId = String(
      stay.roomId ?? stay.roomRowId ?? "UNASSIGNED"
    );

    if (!grouped.has(roomId)) {
      grouped.set(roomId, []);
    }

    grouped.get(roomId).push(stay);
  });

  const bookings = [];
  const laneCounts = {};

  grouped.forEach((roomStays, roomId) => {
    const sorted = [...roomStays].sort(
      (a, b) =>
        String(a.checkIn || "")
          .slice(0, 10)
          .localeCompare(String(b.checkIn || "").slice(0, 10)) ||
        String(a.checkOut || "")
          .slice(0, 10)
          .localeCompare(String(b.checkOut || "").slice(0, 10))
    );

    const laneEndDates = [];

    sorted.forEach((stay) => {
      const start = String(stay.checkIn || "").slice(0, 10);
      const end = String(stay.checkOut || "").slice(0, 10);

      if (!start || !end) {
        bookings.push({ ...stay, _lane: 0 });
        return;
      }

      // Same-day bookings occupy one calendar day.
      // Checkout remains an exclusive boundary.
      const effectiveEnd =
        end <= start
          ? toISODate(addDays(start, 1))
          : end;

      if (!effectiveEnd) {
        bookings.push({ ...stay, _lane: 0 });
        return;
      }

      // A lane is reusable when the previous booking's
      // effective checkout is on or before the next check-in.
      let lane = laneEndDates.findIndex(
        (laneEnd) => laneEnd <= start
      );

      if (lane === -1) {
        lane = laneEndDates.length;
        laneEndDates.push(effectiveEnd);
      } else {
        laneEndDates[lane] = effectiveEnd;
      }

      bookings.push({
        ...stay,
        _lane: lane,
      });
    });

    laneCounts[roomId] = Math.max(1, laneEndDates.length);
  });

  return {
    bookings: bookings.map((stay) => {
      const roomId = String(
        stay.roomId ?? stay.roomRowId ?? "UNASSIGNED"
      );

      return {
        ...stay,
        _lane: stay._lane ?? 0,
        _laneCount: laneCounts[roomId] || 1,
      };
    }),
    laneCounts,
  };
}

export default function OccupancyTimeline({
  rooms = [],
  stays = [],
  startDate,
  view,
  ...props
}) {
  // Month view starts on the first day of the selected month.
  const timelineStartDate = useMemo(() => {
    const date = parseLocalDate(startDate);

    if (!date) {
      const today = new Date();

      if (view === "month") {
        today.setDate(1);
      }

      return getLocalDateString(today);
    }

    if (view === "month") {
      date.setDate(1);
    }

    return getLocalDateString(date);
  }, [startDate, view]);

  // Number of calendar-day columns in each view.
  const dayCount = useMemo(() => {
    if (view === "today") {
      return 1;
    }

    if (view === "week") {
      return 7;
    }

    if (view === "month") {
      const date = parseLocalDate(timelineStartDate);

      if (!date) {
        return 30;
      }

      return new Date(
        date.getFullYear(),
        date.getMonth() + 1,
        0
      ).getDate();
    }

    // Default: fortnight.
    return 14;
  }, [timelineStartDate, view]);

  // Generate the date columns from the same timeline start.
  const dates = useMemo(
    () => makeDateRange(timelineStartDate, dayCount),
    [timelineStartDate, dayCount]
  );

  const [collapsed, setCollapsed] = useState({});

  const {
    bookings: laneBookings,
    laneCounts,
  } = useMemo(
    () => assignBookingLanes(stays),
    [stays]
  );

  // Group rooms by room type.
  const groups = useMemo(() => {
    const groupedRooms = new Map();

    rooms.forEach((room) => {
      const type = room.type || room.roomType || "Other";

      if (!groupedRooms.has(type)) {
        groupedRooms.set(type, []);
      }

      groupedRooms.get(type).push({
        ...room,
        id: room.id ?? room.roomRowId ?? room.roomNumber,
        number: room.number ?? room.roomNumber ?? "Unassigned",
        type,
        capacity: room.capacity ?? room.roomCapacity ?? 0,
      });
    });

    return Array.from(groupedRooms, ([type, grouped]) => ({
      type,
      rooms: grouped,
    }));
  }, [rooms]);

  return (
    <div className="occ-timeline-scroll">
      <div
        className="occ-timeline"
        style={{ minWidth: "max-content" }}
      >
        <TimelineHeader dates={dates} />

        {groups.map((group) => (
          <RoomTypeGroup
            key={group.type}
            group={group}
            collapsed={!!collapsed[group.type]}
            onToggle={() =>
              setCollapsed((current) => ({
                ...current,
                [group.type]: !current[group.type],
              }))
            }
            stays={laneBookings}
            laneCounts={laneCounts}
            dates={dates}
            {...props}
          />
        ))}
      </div>
    </div>
  );
}
